import React, { useState } from "react";
import { auth, db } from "../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ENTRY_CSS, EntryFooter, EntryHeader, Icon, MaroonDrift } from "../components/AugustEntry";

/** What the card says for each door on the landing page (any other value gets the shared sign-in). */
const PORTALS = {
  student: { eyebrow: "Student portal", title: "Student sign-in", description: "Sign in with your TAMU email and password.", label: "TAMU email", placeholder: "you@tamu.edu" },
  board: { eyebrow: "Board member portal", title: "Board member sign-in", description: "Partner companies sign in with the company name and password.", label: "Company name", placeholder: "Your company name" },
  admin: { eyebrow: "Admin portal", title: "Administrator sign-in", description: "TRIP administrators sign in with their email and password.", label: "Email", placeholder: "you@tamu.edu" },
};
const ANY = { eyebrow: "August board meeting", title: "Welcome back", description: "Students and administrators use their email; board members use their company name.", label: "Email or company name", placeholder: "you@tamu.edu or Company Name" };

const NOTES = [
  { icon: Icon.Key, title: "Your login, your portal", body: "Students, board members and administrators each land in their own portal after signing in." },
  { icon: Icon.Mail, title: "Forgot your password?", body: "Students and staff can reset it by email. Board members ask a TRIP administrator." },
  { icon: Icon.Shield, title: "Private to TRIP", body: "The resume book and preferences are visible only to signed-in members of the program." },
];

const LOGIN_CSS = `
  .lg-shell { position: relative; isolation: isolate; overflow: hidden; flex: 1; display: flex; flex-direction: column; }
  .lg-grid { display: grid; grid-template-columns: minmax(0,1fr); align-items: center; gap: 48px; padding-top: 48px; padding-bottom: 48px; flex: 1; }
  @media (min-width: 1024px) { .lg-grid { grid-template-columns: minmax(0,1fr) minmax(0,440px); gap: 80px; padding-top: 64px; padding-bottom: 64px; } }
  .lg-aside { display: none; min-width: 0; }
  @media (min-width: 1024px) { .lg-aside { display: block; } }
  .lg-aside h2 { margin: 12px 0 0; max-width: 28rem; font-size: 34px; line-height: 1.1; font-weight: 600; letter-spacing: -0.035em; text-wrap: balance; }
  .lg-notes { list-style: none; margin: 40px 0 0; padding: 0; max-width: 28rem; display: grid; gap: 24px; }
  .lg-notes li { display: flex; gap: 16px; }
  .lg-notes .ico { width: 36px; height: 36px; flex: none; display: flex; align-items: center; justify-content: center; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1);
                   background: linear-gradient(180deg, #8f2a3c, #500000); box-shadow: inset 0 1px 0 rgba(255,255,255,0.15); }
  .lg-notes b { display: block; font-size: 14.5px; font-weight: 600; }
  .lg-notes span.body { display: block; margin-top: 2px; font-size: 13.5px; line-height: 1.6; color: rgba(255,255,255,0.6); }
  .lg-cardwrap { display: flex; justify-content: center; min-width: 0; }
  @media (min-width: 1024px) { .lg-cardwrap { justify-content: flex-end; } }

  .lg-card { position: relative; width: 100%; max-width: 440px; border-radius: 16px; border: 1px solid rgba(255,255,255,0.2); background: #fff; color: var(--ae-ink); padding: 24px;
             box-shadow: 0 0 0 1px rgba(229,72,77,0.18), 0 40px 90px -30px rgba(0,0,0,0.75), 0 0 80px -20px rgba(143,42,60,0.6); }
  @media (min-width: 640px) { .lg-card { padding: 32px; } }
  .lg-card-hair { position: absolute; left: 40px; right: 40px; top: -1px; height: 2px; border-radius: 999px; background: linear-gradient(90deg, transparent, var(--ae-red), transparent); }
  .lg-card .eyebrow { font-size: 12px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: #9b1c24; }
  .lg-card h1 { margin: 8px 0 0; font-size: 28px; line-height: 1.15; font-weight: 600; letter-spacing: -0.03em; color: var(--ae-ink); }
  .lg-card .desc { margin: 8px 0 0; font-size: 14px; line-height: 1.6; color: var(--ae-muted); }
  .lg-form { margin-top: 24px; display: grid; gap: 16px; }
  .lg-field { display: grid; gap: 6px; }
  .lg-labelrow { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
  .lg-field label { font-size: 13.5px; font-weight: 500; color: var(--ae-ink); }
  .lg-forgot { border: 0; background: none; padding: 0; font: inherit; font-size: 12.5px; color: var(--ae-muted); cursor: pointer; }
  .lg-forgot:hover { color: var(--ae-brand); text-decoration: underline; text-underline-offset: 3px; }
  .lg-input { position: relative; }
  .lg-input input { width: 100%; height: 44px; border: 1px solid #d4d4d8; border-radius: 10px; padding: 0 13px; font: inherit; font-size: 14.5px; color: var(--ae-ink); background: #fff; outline: none; transition: border-color .15s, box-shadow .15s; }
  .lg-input input::placeholder { color: #a1a1aa; }
  .lg-input input:focus { border-color: var(--ae-brand); box-shadow: 0 0 0 3px rgba(80,0,0,0.12); }
  .lg-input .eye { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); border: 0; background: none; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #71717a; cursor: pointer; }
  .lg-input .eye:hover { background: #f3f3f5; color: var(--ae-ink); }
  .lg-submit { margin-top: 4px; height: 46px; border: 0; border-radius: 10px; font: inherit; font-size: 15px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer;
               background: var(--ae-brand); color: #fff; box-shadow: 0 8px 20px -10px rgba(80,0,0,0.7); transition: background .15s; }
  .lg-submit:hover:not(:disabled) { background: var(--ae-brand-hover); }
  .lg-submit:disabled { background: #e4e4e7; color: #a1a1aa; box-shadow: none; cursor: default; }
  .lg-error { margin-top: 16px; border: 1px solid #fecaca; background: #fef2f2; color: #991b1b; border-radius: 10px; padding: 11px 13px; font-size: 13.5px; line-height: 1.5; }
  .lg-foot { margin-top: 24px; padding-top: 16px; border-top: 1px solid var(--ae-line); display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; font-size: 13px; }
  .lg-foot button { border: 0; background: none; padding: 0; font: inherit; cursor: pointer; }
  .lg-foot .back { color: var(--ae-muted); }
  .lg-foot .back:hover { color: var(--ae-ink); }
  .lg-foot .help { color: var(--ae-brand); font-weight: 600; }
  .lg-fine { margin: 14px 0 0; font-size: 12px; line-height: 1.6; color: #71717a; }
`;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const portal = PORTALS[params.get("as")] || ANY;

  function toEmail(input) {
    if (input.includes("@")) return input;
    return input.trim().toLowerCase().replace(/[^a-z0-9]/g, "") + "@trip.board";
  }

  async function handleLogin(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      let cred = null;
      if (email.includes("@")) {
        try { cred = await signInWithEmailAndPassword(auth, email, password); } catch {}
      }
      if (!cred) {
        cred = await signInWithEmailAndPassword(auth, toEmail(email), password);
      }
      const snap = await getDoc(doc(db, "users", cred.user.uid));
      const role = snap.exists() ? snap.data().role : null;
      if (role === "student") navigate("/student");
      else if (role === "board") navigate("/board");
      else if (role === "admin") navigate("/admin");
      else setError("No role assigned. Contact your administrator.");
    } catch { setError("Invalid credentials. Students use email, board members use company name."); }
    setLoading(false);
  }

  async function handleForgotPassword() {
    if (!email) { alert("Enter your email or company name first."); return; }
    if (!email.includes("@")) {
      alert("Board members cannot reset passwords via email. Please contact your administrator.");
      return;
    }
    try {
      const { sendPasswordResetEmail } = await import("firebase/auth");
      await sendPasswordResetEmail(auth, email);
      alert("Password reset email sent to " + email);
    } catch(err) { alert("Error: " + err.message); }
  }

  return (
    <div className="ae">
      <style>{ENTRY_CSS + LOGIN_CSS}</style>
      <div className="lg-shell">
        <MaroonDrift />
        <EntryHeader>
          <button className="ae-navlink" onClick={() => navigate("/august")}>← August <span className="ae-hide-sm">board </span>meeting</button>
        </EntryHeader>
        <main className="ae-wrap lg-grid">
          <aside className="lg-aside ae-enter">
            <p className="ae-eyebrow">August 2026 board meeting</p>
            <h2 className="ae-display">One sign-in for students, board members and <span className="ae-accent">TRIP staff.</span></h2>
            <ul className="lg-notes">
              {NOTES.map(({ icon: NoteIcon, title, body }) => (
                <li key={title}>
                  <span className="ico"><NoteIcon size={16} /></span>
                  <span><b>{title}</b><span className="body">{body}</span></span>
                </li>
              ))}
            </ul>
          </aside>

          <div className="lg-cardwrap">
            <section className="lg-card ae-enter" aria-labelledby="signin-title">
              <div className="lg-card-hair" aria-hidden="true" />
              <div className="eyebrow">{portal.eyebrow}</div>
              <h1 id="signin-title" className="ae-display">{portal.title}</h1>
              <p className="desc">{portal.description}</p>
              {error && <div className="lg-error" role="alert">{error}</div>}
              <form className="lg-form" onSubmit={handleLogin}>
                <div className="lg-field">
                  <label htmlFor="login-email">{portal.label}</label>
                  <div className="lg-input">
                    <input id="login-email" value={email} onChange={e => setEmail(e.target.value)} type="text" required autoFocus placeholder={portal.placeholder} autoComplete="username" />
                  </div>
                </div>
                <div className="lg-field">
                  <div className="lg-labelrow">
                    <label htmlFor="login-password">Password</label>
                    <button type="button" className="lg-forgot" onClick={handleForgotPassword}>Forgot it?</button>
                  </div>
                  <div className="lg-input">
                    <input id="login-password" value={password} onChange={e => setPassword(e.target.value)} type={showPassword ? "text" : "password"} required autoComplete="current-password" style={{ paddingRight: 46 }} />
                    <button type="button" className="eye" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? "Hide password" : "Show password"}>
                      {showPassword ? <Icon.EyeOff size={17} /> : <Icon.Eye size={17} />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="lg-submit" disabled={loading || !email || !password}>
                  {loading ? "Signing in…" : <>Sign in <Icon.Arrow size={16} /></>}
                </button>
              </form>
              <div className="lg-foot">
                <button type="button" className="back" onClick={() => navigate("/august")}>Back to the August board meeting</button>
                {portal !== ANY && <button type="button" className="help" onClick={() => navigate("/login")}>Other portals</button>}
              </div>
              <p className="lg-fine">Students use their TAMU email. Board members use their company name. Signing in is for TRIP students, board members and staff only.</p>
            </section>
          </div>
        </main>
        <EntryFooter />
      </div>
    </div>
  );
}
